import { useState } from 'react';
import {
  Building2,
  ChevronRight,
  Folder,
  FolderOpen,
  HardDrive,
  Layers,
  Library,
  Search,
} from 'lucide-react';
import { StorageNewFolder } from './StorageNewFolder';
import type { PaigeDestinationsBridge, PaigeStorageEntry } from './paige.global';
import { useBrowse, type Browse } from './use-storage-browse';

export interface StorageLocationPickerProps {
  bridge: PaigeDestinationsBridge;
  destinationId: string;
  /** The chosen place's path, empty when none is chosen. */
  path: string;
  /** The search's placeholder at the top, as the provider's search finds things; null: no search. */
  search: string | null;
  onChoose: (entry: { id: string; path: string }) => void;
  /** The fieldset's words; a storage product's folder by default (Nucleus One: its project or folder). */
  words?: { legend: string; choose: string; change: string };
}

const folderWords = { legend: 'Folder', choose: 'Choose folder', change: 'Change folder' };

/**
 * Where a connected storage destination files: the chosen path, and "Choose folder" to browse the
 * connected account through Paige (for Microsoft 365 the person's OneDrive, followed or searched
 * SharePoint sites, their libraries and folders; the drives and folders of the others). Any library
 * or folder can be chosen, including the one open, and "New folder" makes one in the one open.
 */
export function StorageLocationPicker({
  bridge,
  destinationId,
  path,
  search,
  onChoose,
  words = folderWords,
}: StorageLocationPickerProps) {
  const browse = useBrowse(bridge, destinationId);
  /** An entry of the open place, or (no entry) the open place itself. */
  const choose = (entry?: PaigeStorageEntry) => {
    const opened = browse.trail.slice(1);
    const id = entry?.id ?? opened.at(-1)?.id;
    if (!id) return;
    onChoose({
      id,
      path: [...opened.map((s) => s.name), ...(entry ? [entry.name] : [])].join(' / '),
    });
    browse.close();
  };
  return (
    <fieldset className="paige-storage-picker">
      <legend>{words.legend}</legend>
      <span className="paige-storage-picked">
        <FolderOpen size={16} aria-hidden="true" />
        <span>{path || 'Not chosen yet'}</span>
        <button type="button" className="quiet-button small" onClick={() => void browse.open()}>
          {path ? words.change : words.choose}
        </button>
      </span>
      {browse.entries && <PickerList browse={browse} search={search} onChoose={choose} />}
      {browse.error && <small className="paige-destination-error">{browse.error}</small>}
    </fieldset>
  );
}

function PickerList({
  browse,
  search,
  onChoose,
}: {
  browse: Browse;
  search: string | null;
  onChoose: (entry?: PaigeStorageEntry) => void;
}) {
  const here = browse.trail.at(-1);
  return (
    <div className="paige-storage-browse">
      <nav className="paige-storage-trail" aria-label="Where you are">
        {browse.trail.map((step, index) => (
          <button
            key={`${step.id ?? 'top'}-${index}`}
            type="button"
            className="quiet-button small"
            onClick={() => void browse.back(index)}
          >
            {step.name}
          </button>
        ))}
      </nav>
      {browse.trail.length === 1 && search && (
        <PlaceSearch label={search} onSearch={(q) => void browse.search(q)} />
      )}
      <PickerEntries browse={browse} onChoose={onChoose} />
      {here?.id && here.selectable && (
        <span className="paige-storage-here">
          <button type="button" className="quiet-button small" onClick={() => onChoose()}>
            Use {here.name}
          </button>
          <StorageNewFolder onCreate={browse.create} />
        </span>
      )}
    </div>
  );
}

/** What the open place holds: open one, or use it. */
function PickerEntries({
  browse,
  onChoose,
}: {
  browse: Browse;
  onChoose: (entry?: PaigeStorageEntry) => void;
}) {
  return (
    <ul className="paige-storage-entries">
      {browse.entries?.map((entry) => (
        <li key={entry.id}>
          <button
            type="button"
            className="paige-storage-entry"
            onClick={() => void browse.into(entry)}
          >
            <EntryIcon kind={entry.kind} />
            <span>{entry.name}</span>
            <ChevronRight size={14} aria-hidden="true" />
          </button>
          {entry.selectable && (
            <button type="button" className="quiet-button small" onClick={() => onChoose(entry)}>
              Use this
            </button>
          )}
        </li>
      ))}
      {browse.entries?.length === 0 && <li className="paige-storage-empty">Nothing here</li>}
    </ul>
  );
}

function PlaceSearch({ label, onSearch }: { label: string; onSearch: (query: string) => void }) {
  const [query, setQuery] = useState('');
  return (
    <span className="paige-storage-search">
      <Search size={14} aria-hidden="true" />
      <input
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        onKeyDown={(event) => {
          if (event.key !== 'Enter') return;
          event.preventDefault();
          onSearch(query);
        }}
        placeholder={label}
        aria-label={label}
      />
      <button type="button" className="quiet-button small" onClick={() => onSearch(query)}>
        Find
      </button>
    </span>
  );
}

function EntryIcon({ kind }: { kind: PaigeStorageEntry['kind'] }) {
  if (kind === 'site' || kind === 'organization') return <Building2 size={14} aria-hidden="true" />;
  if (kind === 'project') return <Layers size={14} aria-hidden="true" />;
  if (kind === 'library') return <Library size={14} aria-hidden="true" />;
  if (kind === 'drive') return <HardDrive size={14} aria-hidden="true" />;
  return <Folder size={14} aria-hidden="true" />;
}
